import LayoutContext, { ILayoutSizes } from "contexts/LayoutContext";
import styles from "./styles.module.scss";
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  getPanelElement,
} from "react-resizable-panels";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Props } from "./typings";
import { useParams } from "react-router-dom";
import { getListName } from "utils/getListName";
import SearchBar from "components/SearchBar";
import useFilterMetaData from "hooks/useFilterMetaData";
import { useAuth } from "react-oidc-context";
import { getUserId } from "utils/getUserId";
import { ListType } from "dto/ListType";
import WorkspaceContext from "contexts/CurrentWorkspace";
import AppContext from "contexts/AppContext";
import { logger } from "utils/logger";
import { useMediaQuery } from "usehooks-ts";
import ModalsManagerContext from "contexts/ModalsManager";
import { useTranslation } from "react-i18next";

const PanelsLayout: FC<Props> = ({
  layoutName = "layout",
  defaultLeft = 35,
  minLeft,
  isExpanded = true,
  disableHeader = false,
  disableSearch = false,
  collapseRight = false,
  disablePadding = false,
  disableListStyle = false,
  disablePaddingScroll = false,
  onChangeSearch,
  leftPanel,
  rightPanel,
  leftClassName = "",
  resize = false,
  wrapperClassName = "",
  title,
  subTitle,
  hideRight = false,
  allowRightPanelOverflow = false,
  trackActivePanel = false,
  headerRight,
  isSearchExpanded = false,
  isRightActive = true,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 767px)");
  useContext(ModalsManagerContext);
  const { packageId, caseId } = useParams();
  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const ref = useRef(null);
  const rightPanelRef = useRef(null);

  const [panelsInitialized, setPanelsInitialized] = useState(false);
  const { filterId } = useParams();

  const defaultSizeRight = !hideRight ? 100 - defaultLeft : 0;

  const isWork =
    filterId === "C2192A34-C79B-4856-8D0B-02DA1C49C5F9" ||
    filterId === "027bdcf6-232b-4355-a396-2558c2c30f23" ||
    filterId === "675d78ff-983d-4b0e-a334-67699448572a" ||
    filterId === "A666CC3E-A2AF-49AA-AB95-54DA6CB4CC99";

  const isClients = window.location.pathname.search("clients") !== -1;
  const isTemplates = window.location.pathname.search("templates") !== -1;
  const isDrive = window.location.pathname.search("drive") !== -1;
  const isShares = window.location.pathname.search("shares") !== -1;
  const isCases = window.location.pathname.search("cases") !== -1;
  const isOutcoming = window.location.pathname.search("outcoming") !== -1;

  const getListType = () => {
    if (isCases) return ListType.Cases;
    if (isClients) return ListType.Participants;
    if (isDrive) return ListType.Drive;
    if (isShares) return ListType.Shares;
    if (isTemplates) return ListType.Templates;

    if (isOutcoming) return ListType.Outgoing;

    return ListType.Incoming;
  };

  const browserPath = window.location.pathname;
  const show = !isClients && !isTemplates;
  const [, setLastPage] = useState("");

  const [sizes, setSizes] = useState<ILayoutSizes>({
    width: 0,
    height: 0,
    isDraging: false,
  });

  const { filterId: workspaceFilterId } = useContext(WorkspaceContext);
  const { setActivePanel } = useContext(AppContext);

  const { listId, listName } = useFilterMetaData({
    listType: getListType(),
    filterId: filterId,
    workspaceFilterId,
  });

  const handleExpandMode = () => {
    if (isMobile) return;

    if (!isExpanded) {
      const sizeLeft = defaultLeft;
      const sizeRight = 100 - sizeLeft;

      if (!panelsInitialized) return;
      ref?.current?.setLayout([sizeLeft, sizeRight]);
      return;
    }
    const settings = localStorage.getItem(`${userId}.${layoutName}`);
    console.log(settings);
    const parsedSettings = JSON.parse(settings) ?? { leftPanel: defaultLeft };
    const sizeLeft =
      Number(parsedSettings.leftPanel) <= 6
        ? defaultLeft
        : Number(parsedSettings.leftPanel);
    const sizeRight = 100 - sizeLeft;

    if (!panelsInitialized) return;

    //@ts-ignore
    ref?.current?.setLayout([sizeLeft, sizeRight]);
  };

  const onLayout = (size: number[]) => {
    if (!panelsInitialized) {
      const settings = localStorage.getItem(`${userId}.${layoutName}`);
      if (!settings?.length) {
        setPanelsInitialized(true);
        return;
      }

      if (hideRight || isMobile) {
        setSizes({
          ...sizes,
          width: 100,
          height: 0,
        });
        return;
      }

      const parsedSettings = JSON.parse(settings);
      setSizes({
        ...sizes,
        width: Number(parsedSettings.leftPanel),
        height: Number(parsedSettings.rightPanel),
      });
      //@ts-ignore
      if (parsedSettings.leftPanel + parsedSettings.rightPanel === 100) {
        ref?.current?.setLayout([
          parsedSettings.leftPanel,
          parsedSettings.rightPanel,
        ]);
      }
      setPanelsInitialized(true);
      return;
    }

    setSizes({
      ...sizes,
      width: Number(size[0]),
      height: Number(size[1]),
    });
    localStorage.setItem(
      `${userId}.${layoutName}`,
      JSON.stringify({ leftPanel: size[0], rightPanel: size[1] }),
    );
  };

  useEffect(() => {
    if (ref?.current) return;
    // @ts-ignore
    if (collapseRight || isMobile) ref?.current?.setLayout([100, 0]);
  }, [ref, collapseRight, isMobile]);

  useEffect(() => {
    if (!ref?.current) return;
    if (layoutName !== "main" && layoutName !== "Folders-dossier") return;
    handleExpandMode();
  }, [isExpanded, panelsInitialized]);

  useEffect(() => {
    if (!ref?.current) return;
    if (sizes.height === 100) {
      setLastPage(browserPath);
      setSizes({
        ...sizes,
        width: 50,
        height: 50,
      });
      // @ts-ignore
      ref?.current?.setLayout([50, 50]);
    }
  }, [t]);

  useEffect(() => {
    if (!resize) return;
    if (isMobile) return;

    if (packageId && sizes.width === 100) {
      setSizes({
        ...sizes,
        width: 50,
        height: 50,
      });
      //@ts-ignore
      ref?.current?.setLayout([50, 50]);
    }

    if (caseId && sizes.width === 100) {
      setSizes({
        ...sizes,
        width: 50,
        height: 50,
      });
      //@ts-ignore
      ref?.current?.setLayout([50, 50]);
    }
  }, [packageId, caseId]);

  useEffect(() => {
    if (!trackActivePanel) return;

    const rightPanelElement = getPanelElement("right-panel");
    rightPanelRef.current = rightPanelElement;

    if (rightPanelElement) {
      rightPanelElement.tabIndex = 1;
      rightPanelElement.onfocus = () => {
        onActivate("right");
      };
    }
  }, []);

  const onActivate = (name: string) => {
    if (!trackActivePanel) return;
    setActivePanel(name);
    logger.log("Active panel:", name);
  };

  return (
    <div className={`${styles.wrap} ${wrapperClassName}`}>
      <LayoutContext.Provider
        value={{
          sizes,
          setSizes,
        }}
      >
        <PanelGroup
          ref={ref}
          direction="horizontal"
          onLayout={onLayout}
          style={{ overflow: "visible" }}
        >
          {(!isMobile || !isRightActive) && (
            <Panel
              style={{
                overflow:
                  isExpanded || layoutName !== "main" ? "hidden" : "visible",
                height: "100%",
              }}
              defaultSize={isExpanded ? defaultLeft : 20}
              minSize={minLeft}
            >
              {!disableHeader && (
                <div className={styles.header}>
                  {/* {isMobile && (
                    <div className={styles.tools}>
                      <div className={styles.menuIcon}>
                        <MenuIcon
                          onClick={() => setModals({ ...modals, menu: true })}
                        ></MenuIcon>
                      </div>
                    </div>
                  )} */}
                  <div className={styles.left}>
                    <div
                      className={`${styles.title} ${
                        !subTitle && styles.remove
                      }`}
                    >
                      {title ?? getListName(listId ?? 0, false, isWork, t)}
                    </div>
                    <div className={styles.subTitle}>
                      {subTitle ? subTitle : <>{show && listName}</>}
                    </div>
                  </div>
                  <div className={styles.right}>
                    {headerRight}
                    {!disableSearch && (
                      <SearchBar
                        isExpanded={isSearchExpanded}
                        onChange={(t, statusId, tagId, entityId, kind) =>
                          onChangeSearch?.(t, statusId, tagId, entityId, kind)
                        }
                      />
                    )}
                  </div>
                </div>
              )}
              <div
                className={`${!disableListStyle && styles.list} ${
                  disablePadding && styles.disablePadding
                } ${leftClassName} ${
                  disablePaddingScroll && styles.disableList
                }`}
                tabIndex={0}
                onFocus={() => onActivate("left")}
              >
                {leftPanel}
              </div>
            </Panel>
          )}
          {!isMobile && (
            <PanelResizeHandle
              disabled={layoutName === "main" && !isExpanded}
              onDragging={(isDraging) => {
                if (!isExpanded) return;
                setSizes({ ...sizes, isDraging });
              }}
              className={`${styles.test} ${
                disablePaddingScroll && styles.disablePaddingScroll
              }`}
              id={layoutName}
            ></PanelResizeHandle>
          )}

          {!hideRight && (!isMobile || isRightActive) && (
            <Panel
              id={trackActivePanel ? "right-panel" : "right-panel-untrack"}
              defaultSize={defaultSizeRight}
              style={{
                overflow: allowRightPanelOverflow ? "visible" : "hidden",
                overflowX: "hidden",
              }}
              ref={rightPanelRef}
            >
              {rightPanel}
            </Panel>
          )}
        </PanelGroup>
      </LayoutContext.Provider>
    </div>
  );
};

export default PanelsLayout;
